import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/TopNav";
import { PageHeader, Kpi, Delta, EmptyState } from "@/components/common";
import { DonutAllocation, BarBreakdown, AreaTrend, CHART_COLORS } from "@/components/Charts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { fmtUSD, fmtPct } from "@/lib/format";
import { ProjectionCalculator } from "@/components/ProjectionCalculator";
import { AutoInvestPlans } from "@/components/invest/AutoInvestPlans";
import { PieChart as PieIcon, TrendingUp, RefreshCw, Bell } from "lucide-react";

const QUESTIONS = [
  { q: "What is your primary investment goal?", opts: [["Preserve capital", 1], ["Steady income", 2], ["Balanced growth", 3], ["Maximise growth", 4]] },
  { q: "Your investment horizon?", opts: [["< 3 years", 1], ["3-7 years", 2], ["7-15 years", 3], ["15+ years", 4]] },
  { q: "If your portfolio dropped 20%, you would:", opts: [["Sell everything", 1], ["Sell some", 2], ["Hold", 3], ["Buy more", 4]] },
  { q: "Your investment experience?", opts: [["None", 1], ["Some", 2], ["Experienced", 3], ["Professional", 4]] },
];

export function InvestModule({ kind = "invest" }) {
  const { refreshUser } = useApp();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [rec, setRec] = useState(null);
  const [amount, setAmount] = useState(3000);
  const [autoInvest, setAutoInvest] = useState(false);
  const [portfolios, setPortfolios] = useState([]);
  const [perf, setPerf] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadPortfolios = useCallback(async () => {
    const { data } = await api.get(`/invest/portfolios?kind=${kind}`);
    setPortfolios(data);
    if (data[0]) { const p = await api.get(`/invest/performance/${data[0].id}?days=90`); setPerf(p.data); }
  }, [kind]);
  useEffect(() => { loadPortfolios(); }, [loadPortfolios]);

  const answer = (val) => {
    const next = [...answers]; next[step] = val; setAnswers(next);
    if (step < QUESTIONS.length - 1) setStep(step + 1);
    else getRecommendation(next);
  };

  const getRecommendation = async (ans) => {
    const raw = ans.reduce((a, b) => a + b, 0); // 4..16
    const score = Math.max(1, Math.min(10, Math.round(((raw - 4) / 12) * 9) + 1));
    const { data } = await api.post("/invest/recommend", { risk_score: score, halal: kind === "halal" });
    setRec(data);
  };

  const invest = async () => {
    setBusy(true);
    try {
      await api.post("/invest/portfolios", { name: `${rec.profile} ${kind === "halal" ? "Halal" : ""} Portfolio`.trim(),
        allocation: rec.allocation, amount_usd: Number(amount), auto_invest: autoInvest, auto_invest_amount: autoInvest ? Number(amount) : 0, kind });
      toast.success("Portfolio funded", { description: `${fmtUSD(amount)} invested across ${rec.allocation.length} ETFs` });
      setRec(null); setStep(0); setAnswers([]);
      await Promise.all([loadPortfolios(), refreshUser()]);
    } catch (e) { toast.error("Investment failed", { description: e?.response?.data?.detail || "Convert QAR to USD in Wallet first" }); }
    finally { setBusy(false); }
  };

  const allocData = rec ? rec.allocation.map((a) => ({ name: a.symbol, value: Math.round(a.weight * 100) })) : [];

  return (
    <div className="space-y-4 sm:space-y-5">
      <ProjectionCalculator halal={kind === "halal"} />
      <div className="grid gap-4 sm:gap-5 lg:grid-cols-2">
      {/* Questionnaire / Recommendation */}
      <Card className="p-5 shadow-premium">
        {!rec ? (
          <div data-testid={`${kind}-risk-questionnaire`}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium">Risk Questionnaire</div>
              <Badge variant="secondary">Step {step + 1}/{QUESTIONS.length}</Badge>
            </div>
            <Progress value={((step) / QUESTIONS.length) * 100} className="mb-5" />
            <div className="text-lg font-medium mb-4">{QUESTIONS[step].q}</div>
            <div className="space-y-2">
              {QUESTIONS[step].opts.map(([label, val]) => (
                <button key={label} onClick={() => answer(val)}
                  className="w-full text-start rounded-lg border px-4 py-3 hover:bg-accent hover:border-[color:var(--tharwy-lime)] transition-colors"
                  data-testid={`quiz-option-${val}`}>
                  {label}
                </button>
              ))}
            </div>
            {step > 0 && <Button variant="ghost" size="sm" className="mt-4" onClick={() => setStep(step - 1)}>Back</Button>}
          </div>
        ) : (
          <div data-testid={`${kind}-recommendation-card`}>
            <div className="flex items-center justify-between">
              <div><div className="text-sm text-muted-foreground">Recommended profile</div><div className="text-2xl font-semibold">{rec.profile}</div></div>
              <Button variant="ghost" size="sm" onClick={() => { setRec(null); setStep(0); setAnswers([]); }}>Retake</Button>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="rounded-lg bg-secondary/60 p-3"><div className="text-xs text-muted-foreground">Expected return</div><div className="numeric text-lg font-semibold text-emerald-600">{fmtPct(rec.expected_return)}</div></div>
              <div className="rounded-lg bg-secondary/60 p-3"><div className="text-xs text-muted-foreground">Volatility</div><div className="numeric text-lg font-semibold">{rec.expected_volatility}%</div></div>
            </div>
            <div className="mt-4"><DonutAllocation data={allocData} height={180} /></div>
            <div className="mt-3 space-y-1.5">
              {rec.allocation.map((a, i) => (
                <div key={`${a.symbol}-${i}`} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} /><span className="font-medium">{a.symbol}</span><span className="text-muted-foreground text-xs truncate max-w-[120px]">{a.name}</span></div>
                  <span className="numeric">{Math.round(a.weight * 100)}%</span>
                </div>
              ))}
            </div>
            <div className="mt-5 space-y-3 border-t pt-4">
              <div className="space-y-1.5">
                <div className="flex justify-between"><Label>Investment amount (USD)</Label><span className="numeric text-sm">{fmtUSD(amount)}</span></div>
                <Slider min={100} max={20000} step={100} value={[amount]} onValueChange={(v) => setAmount(v[0])} data-testid={`${kind}-amount-slider`} />
                <Input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} data-testid={`${kind}-amount-input`} />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-secondary/60 px-3 py-2">
                <div><div className="text-sm font-medium">Auto-invest monthly</div><div className="text-xs text-muted-foreground">Recurring contribution</div></div>
                <Switch checked={autoInvest} onCheckedChange={setAutoInvest} data-testid={`${kind}-auto-invest-toggle`} />
              </div>
              <Button className="w-full" onClick={invest} disabled={busy} data-testid={`${kind}-fund-portfolio`}>{busy ? "Investing..." : `Invest ${fmtUSD(amount)}`}</Button>
            </div>
          </div>
        )}
      </Card>

      {/* Portfolios + performance */}
      <div className="space-y-4 sm:space-y-5">
        <AutoInvestPlans kind={kind} portfolios={portfolios} onChanged={async () => { await loadPortfolios(); await refreshUser(); }} />
        <Card className="p-5 shadow-premium">
          <div className="flex items-center gap-2 text-sm font-medium mb-3"><TrendingUp className="h-4 w-4" />Performance</div>
          {perf.length ? <AreaTrend data={perf} dataKey="value" color="#5C7020" height={220} /> :
            <EmptyState title="No portfolio yet" hint="Complete the questionnaire and invest to track performance" />}
        </Card>
        <Card className="p-5 shadow-premium">
          <div className="text-sm font-medium mb-3">Your Portfolios</div>
          {portfolios.length ? (
            <div className="space-y-2">
              {portfolios.map((p) => (
                <div key={p.id} className="rounded-lg border p-3" data-testid="portfolio-card">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{p.name}</div>
                    {p.needs_rebalance && <Badge className="gap-1" style={{ background: "rgba(180,139,46,0.14)", color: "#5C7020" }}><Bell className="h-3 w-3" />Rebalance</Badge>}
                  </div>
                  <div className="flex items-center justify-between mt-2 text-sm">
                    <span className="text-muted-foreground">Value</span><span className="numeric font-medium">{fmtUSD(p.current_value_usd)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">P&amp;L</span><Delta value={p.pnl_pct} />
                  </div>
                </div>
              ))}
            </div>
          ) : <EmptyState title="No portfolios" hint="Your managed portfolios will appear here" />}
        </Card>
      </div>
      </div>
    </div>
  );
}

export default function Invest() {
  return (
    <AppShell>
      <PageHeader title="Invest" subtitle="Personalised passive ETF portfolios, managed for you" testid="invest-header" />
      <InvestModule kind="invest" />
    </AppShell>
  );
}
